import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Icon,
  TooltipComponent,
  UserAvatar,
} from "../../components/Component";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import { findUpper } from "../../utils/Utils";
import miscService from "../../api/misc/misc";
import dayjs from "dayjs";
import { Flex, Spin } from "antd";
import { useNavigate, useLocation } from "react-router";
import { LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { logHoursApis } from "../../api/TimeLog";
import Swal from "sweetalert2";

interface NotificationParams {
  taskId?: number;
  issueId?: number;
  subTaskId?: number;
  projectId?: number;
  projectName?: string;
  taskName?: string;
  userName?: string;
  taskSequenceId?: string;
  timeLogId?: number;
  entity?: string;
  entityId?: number;
  entityName?: string;
  params?: NotificationParams;
  comment?: string;
  derivedProjectStatus?: string;
}

interface NotificationData {
  notificationId: number;
  message: string;
  moduleType: string;
  moduleName: string;
  moduleId: number;
  params: NotificationParams;
  messageTitle: string;
  senderName: string;
  createdAt: string;
  senderLogo?: string;
  viewed: boolean;
}

const InboxMessages: React.FC<any> = ({
  mailId,
  toggleMessageView,
  onDeleteNotification,
}) => {
  const [mailData, setMailData] = useState<NotificationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);

  const { data: RootPageRenderState } = useSelector(
    (state: any) => state.RootPageRenderState
  );

  // Common navigation state for back button support
  const getNavigationState = (additionalState: any = {}) => ({
    ...additionalState,
    returnPath: location.pathname,
    returnState: location.state,
    fromNotifications: true,
  });

  useEffect(() => {
    // console.log("mailId", mailId);
    if (mailId) {
      fetchNotificationById();
    }
  }, [mailId]);

  // Mark notification as viewed when mailData is loaded and not already viewed
  useEffect(() => {
    if (mailId && mailData && !mailData.viewed) {
      markNotificationAsViewed();
    }
  }, [mailData, mailId]);

  const markNotificationAsViewed = async () => {
    if (mailId) {
      try {
        await miscService.notificationRead(mailId);
      } catch (err) {
        console.log("Error marking notification as viewed:", err);
      }
    }
  };

  const fetchNotificationById = async () => {
    setLoading(true);
    try {
      const { response, status } = await miscService.viewNotificationById(
        mailId
      );
      if (status) {
        setMailData(response);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeLogDetailsForNotification = async (timeLogId: number) => {
    try {
      // console.log("Fetching time log details for ID:", timeLogId);
      const response = await logHoursApis.fetchTimelogById(timeLogId);

      if (response?.status && response?.data) {
        const timeLogData = response.data;

        // Format the time log data for the modal
        const formattedTimeLog = {
          timeLogId: timeLogData.timeLogId,
          timeLogTitle: `Time Log #${timeLogData.timeLogId}`,
          projectId: timeLogData.projectId,
          projectName: timeLogData.projectName,
          projectTitle:
            timeLogData.projectName || `Project #${timeLogData.projectId}`,
          taskId: timeLogData.taskId,
          taskName: timeLogData.taskName,
          issueId: timeLogData.issueId,
          issueName: timeLogData.issueName,
          userId: timeLogData.userId,
          userName: timeLogData.userName,
          loginTime: timeLogData.loginTime,
          logoutTime: timeLogData.logoutTime,
          logDate: timeLogData.logDate,
          totalWorkHoursInSec: timeLogData.totalWorkHoursInSec,
          billingType: timeLogData.billingType,
          notes: timeLogData.notes,
          shiftName: timeLogData.shiftName,
          approvalStatus: timeLogData.approvalStatus,
          createdBy: timeLogData.createdBy,
          createdAt: timeLogData.createdAt,
          timeLogResources: timeLogData.timeLogResources || [],
          orgId: timeLogData.orgId,
        };

        return formattedTimeLog;
      } else {
        console.error("Failed to fetch time log details:", response);
        return null;
      }
    } catch (error) {
      console.error("Error fetching time log details:", error);
      return null;
    }
  };

  const getScreenNameFromStatus = (derivedProjectStatus: string): string => {
    const statusMap: Record<string, string> = {
      "Pending+Approval": "Pending Approval",
    };

    return statusMap[derivedProjectStatus];
  };

  // Handle redirection based on module type
  const handleRedirect = async () => {
    if (!mailData?.params) {
      console.log("No params found in mailData");
      return;
    }

    const { moduleType, params } = mailData;

    // Handle nested params structure (for CommentAdded notifications)
    const nestedParams: any = params?.params || params;
    const {
      projectId,
      projectName,
      taskId,
      issueId,
      subTaskId,
      timeLogId,
      entity,
      entityId,
      entityName,
      derivedProjectStatus,
    } = nestedParams || params;

    switch (moduleType) {
      case "PredecessorTask":
      case "TaskCompleted":
      case "TaskStatusUpdated":
      case "NewTaskAssigned":
      case "ChecklistUpdated":
        // Navigate to task detail page
        if (taskId) {
          navigate(`/pms/project/task-detail/${taskId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        }
        break;

      // Subtask related notifications - navigate to parent task detail
      case "SubtaskAssigned":
        if (taskId) {
          navigate(`/pms/project/task-detail/${taskId}`, {
            state: getNavigationState({
              projectId,
              projectName,
              subTaskId,
            }),
          });
        }
        break;

      // Issue related notifications
      case "NewIssueRaised":
      case "IssueResolved":
        if (issueId) {
          navigate(`/pms/project/issue-detail/${issueId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        }
        break;

      // Project related notifications - For Your Information
      case "ProjectLive":
      case "ProjectApproved":
        if (projectId) {
          navigate(`/pms/project/view`, {
            state: getNavigationState({
              id: projectId,
              projectId,
              projectName,
            }),
          });
        }
        break;

      // Baseline created notification - redirect to tasks with Gantt view
      case "BaselineCreated":
        if (projectId) {
          navigate(`/pms/project/tasks`, {
            state: getNavigationState({
              id: projectId,
              projectId,
              projectName,
              listViewType: "gantt",
            }),
          });
        }
        break;

      // TimeLog related notifications - navigate to timelog detail page
      case "TimeLogSubmitted":
      case "TimeLogApproved":
        if (timeLogId) {
          navigate(`/pms/project/timelog-detail/${timeLogId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        }
        break;

      // Comment related notifications
      case "CommentAdded":
        // Handle nested params structure for comments
        const commentEntity = entity || params?.entity || nestedParams?.entity;
        const commentEntityId =
          entityId || params?.entityId || nestedParams?.entityId;
        const commentEntityName =
          entityName || params?.entityName || nestedParams?.entityName;
        const commentProjectId =
          commentEntity === "Project"
            ? commentEntityId || projectId || nestedParams?.projectId
            : projectId || nestedParams?.projectId || params?.params?.projectId;

        // For Project comments, navigate to project view
        if (commentEntity === "Project" && commentProjectId) {
          navigate(`/pms/project/view`, {
            state: getNavigationState({
              id: commentProjectId,
              projectId: commentProjectId,
              projectName: commentEntityName || projectName,
              activeTab: "comments",
            }),
          });
        }
        // For Task comments, navigate to task detail page
        else if (taskId || (commentEntity === "Task" && commentEntityId)) {
          const taskIdToUse = taskId || commentEntityId;
          navigate(`/pms/project/task-detail/${taskIdToUse}`, {
            state: getNavigationState({
              projectId: commentProjectId,
              projectName,
              activeTab: "comments",
            }),
          });
        }
        // For Issue comments, navigate to issue detail page
        else if (issueId || (commentEntity === "Issue" && commentEntityId)) {
          const issueIdToUse = issueId || commentEntityId;
          navigate(`/pms/project/issue-detail/${issueIdToUse}`, {
            state: getNavigationState({
              projectId: commentProjectId,
              projectName,
              activeTab: "comments",
            }),
          });
        }
        break;

      // Approval related notifications
      case "ProjectApproval":
        if (projectId) {
          navigate(`/pms/pending-approvals`, {
            state: getNavigationState({
              id: projectId,
              projectId,
              projectName,
              derivedProjectStatus,
              notificationData: mailData,
              extra: {
                plannedStartDate: nestedParams?.plannedStartDate,
                plannedEndDate: nestedParams?.plannedEndDate,
              },
            }),
          });
        }
        break;

      case "ChecklistApproval":
      case "TaskApproval":
        // Navigate to task detail page for approval
        if (taskId) {
          navigate(`/pms/project/task-detail/${taskId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        }
        break;

      case "IssueApproval":
        if (issueId) {
          navigate(`/pms/project/issue-detail/${issueId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        }
        break;

      case "TimeLogApproval":
        // Navigate to work approvals page
        navigate("/pms/project/workapprovals", {
          state: getNavigationState({
            id: projectId,
            projectId,
            projectName,
            notificationId: mailData.notificationId,
            notificationData: mailData,
            moduleType,
            ...(taskId && { taskId }),
            ...(issueId && { issueId }),
            ...(timeLogId && { timeLogId }),
          }),
        });
        break;

      // Reminder notifications - redirect based on entity type
      case "Reminder":
        if (taskId) {
          navigate(`/pms/project/task-detail/${taskId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        } else if (issueId) {
          navigate(`/pms/project/issue-detail/${issueId}`, {
            state: getNavigationState({
              projectId,
              projectName,
            }),
          });
        }
        break;

      default:
        console.warn("No specific handler for module type:", moduleType);
        break;
    }
  };

  const deleteInbox = (id: any) => {
    // console.log("Delete notification:", id);
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        onDeleteNotification(id);
        toggleMessageView();
        Swal.fire({
          title: "Deleted!",
          text: "Your notification has been deleted.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    });
  };

  return (
    <>
      {loading ? (
        <Flex align="center" gap="middle" className="pageLoader">
          <Spin
            indicator={<LoadingOutlined spin />}
            className="m-auto"
            size="large"
          />
        </Flex>
      ) : (
        <>
          <div className="nk-ibx-head">
            <div className="nk-ibx-head-actions">
              <ul className="nk-ibx-head-tools g-1">
                <li className="ms-n2" onClick={() => toggleMessageView()}>
                  <a
                    href="#item"
                    className="btn btn-icon btn-trigger nk-ibx-hide"
                  >
                    <Icon name="arrow-left"></Icon>
                  </a>
                </li>
                <li onClick={() => deleteInbox(mailData?.notificationId)}>
                  <TooltipComponent
                    tag="a"
                    iconClass=""
                    containerClassName="btn btn-icon btn-trigger"
                    icon="trash"
                    id="ibx-msg-trash"
                    direction="top"
                    text="Delete"
                  />
                </li>
              </ul>
            </div>
          </div>
          <SimpleBar
            className="nk-ibx-reply nk-reply"
            scrollableNodeProps={{ ref: messagesEndRef }}
          >
            <div className="nk-ibx-reply-head">
              <div>
                <h4 className="title">
                  {mailData?.messageTitle || "Notification"}
                </h4>
                <ul className="nk-ibx-tags g-1">
                  <li className="btn-group is-tags" key={mailData?.moduleId}>
                    <Button color="primary" size="sm" className="btn-dim">
                      {mailData?.moduleName}
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
            <div className="nk-ibx-reply-group">
              <div className="nk-ibx-reply-item nk-reply-item">
                <div className={`nk-reply-header nk-ibx-reply-header`}>
                  <div className="nk-reply-desc">
                    <UserAvatar
                      size="sm"
                      type="square"
                      className="nk-reply-avatar"
                      text={
                        mailData?.senderName
                          ? findUpper(mailData.senderName)
                          : ""
                      }
                      theme=""
                      icon="user"
                      image={mailData?.senderLogo || ""}
                      imageAlt="user"
                    />
                    <div className="nk-reply-info">
                      <div className="nk-reply-author lead-text">
                        {mailData?.senderName}
                      </div>
                    </div>
                  </div>
                  <ul className="nk-reply-tools g-1">
                    <li className="date-msg">
                      <span className="date">
                        {dayjs(mailData?.createdAt).format("DD-MM-YYYY HH:mm")}
                      </span>
                    </li>
                  </ul>
                </div>
                <div className={`nk-reply-body nk-ibx-reply-body is-shown`}>
                  <div
                    className="nk-reply-entry entry"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <p>Hello,</p>
                    <br />
                    <p>{mailData?.message}</p>
                    <br />
                    <p>Thank you.</p>
                  </div>
                </div>
                <br />
                <div className="alert alert-info" role="alert">
                  <Icon name="info"></Icon>
                  <span>
                    {" "}
                    <a
                      href="#"
                      className="alert-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // console.log("Current mailData:", mailData);
                        // console.log("mailData?.params:", mailData?.params);
                        if (mailData?.params) {
                          handleRedirect();
                        } else {
                          console.error("No params found in mailData");
                        }
                      }}
                    >
                      {`Click here to see the details for ${mailData?.moduleName || "this notification"
                        }`}
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </SimpleBar>
        </>
      )}
    </>
  );
};

export default InboxMessages;
